import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';

// API base + auth token, matching the pattern used by FactoryManagementPanel.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

// Convert an ISO `YYYY-MM-DD` date into the readable `15 Jan 2025` form used in the table.
const ymdToReadable = (ymdStr?: string): string => {
  if (!ymdStr) return 'N/A';
  const parts = ymdStr.split('T')[0].split('-');
  if (parts.length !== 3) return ymdStr;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthName = months[(parseInt(parts[1], 10) || 1) - 1] || 'Jan';
  const day = parseInt(parts[2], 10).toString().padStart(2, '0');
  return `${day} ${monthName} ${parts[0]}`;
};

// Shape of one processed Licensing Ledger row.
interface LicensingRow {
  name: string;
  region: string;
  subStatus: 'Active' | 'Trial' | 'Grace' | 'Expired';
  start: string;
  end: string;
  daysRemaining: number;
  allowedUsers: number;
}

export interface FactoryData {
  name: string;
  region: string;
  inspections: number;
  usersCount: number;
  storageGB: number;
  appVersion: string;
  outdatedDevices: number;
  status: 'Optimal' | 'Warning' | 'Critical';
  uptime: string;
  lastInspectionLabel: string;
  daysSinceLastInspection: number;
  subscriptionStatus?: 'Active' | 'Trial' | 'Grace' | 'Expired';
  subscriptionStart?: string;
  subscriptionEnd?: string;
  allowedUsers?: number;
}

interface SubscriptionAnalyticsPanelProps {
  // Retained for backward compatibility with the cockpit; the panel now derives
  // every card and the ledger table from live API data instead of this prop.
  factories?: FactoryData[];
}

export const SubscriptionAnalyticsPanel: React.FC<SubscriptionAnalyticsPanelProps> = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');

  // Unified standard date offset calculation helper
  const getDaysRemaining = (endDateStr?: string): number => {
    if (!endDateStr) return 0;
    try {
      const end = new Date(endDateStr);
      const today = new Date("2026-06-18T00:00:00"); // Standard current reference date from environment
      end.setHours(0,0,0,0);
      today.setHours(0,0,0,0);
      const diffTime = end.getTime() - today.getTime();
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch (e) {
      return 0;
    }
  };

  // --- Licensing Log & Contracts Ledger table + KPI cards: real data from the organizations API ---
  const [ledgerRows, setLedgerRows] = useState<LicensingRow[]>([]);
  const [isLoadingLedger, setIsLoadingLedger] = useState(true);
  const [ledgerError, setLedgerError] = useState<string | null>(null);

  // Seat distribution (admin vs mobile) comes from the cockpit stats endpoint.
  const [adminUserCount, setAdminUserCount] = useState(0);
  const [mobileUserCount, setMobileUserCount] = useState(0);

  // --- KPI cards (top row): derived from the live licensing ledger ---
  // Nearing expiry: a contract whose end date is within 15 days (and not already expired).
  const nearingExpiryList = ledgerRows.filter(f => f.daysRemaining > 0 && f.daysRemaining <= 15 && f.subStatus !== 'Expired');
  const activeCount = ledgerRows.filter(f => f.subStatus === 'Active').length;
  const trialCount = ledgerRows.filter(f => f.subStatus === 'Trial').length;
  const graceCount = ledgerRows.filter(f => f.subStatus === 'Grace').length;
  const totalSeats = adminUserCount + mobileUserCount;

  useEffect(() => {
    let cancelled = false;

    const fetchLedger = async () => {
      setIsLoadingLedger(true);
      setLedgerError(null);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/v1/organizations`, {
          headers: { ...(token && { Authorization: `Bearer ${token}` }) },
        });
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.message || 'Failed to fetch organizations');

        const rows: LicensingRow[] = (data.data || []).map((org: any): LicensingRow => ({
          name: org.name,
          region: org.location_district || '',
          subStatus: (org.status as LicensingRow['subStatus']) || 'Active',
          start: ymdToReadable(org.start_date),
          end: ymdToReadable(org.expiry_date),
          daysRemaining: getDaysRemaining(org.expiry_date),
          allowedUsers: org.seats || 20,
        }));

        if (!cancelled) setLedgerRows(rows);
      } catch (err: any) {
        if (!cancelled) setLedgerError(err.message || 'Failed to load licensing ledger');
      } finally {
        if (!cancelled) setIsLoadingLedger(false);
      }
    };

    const fetchSeatDistribution = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/v1/organizations/stats/cockpit`, {
          headers: { ...(token && { Authorization: `Bearer ${token}` }) },
        });
        const data = await response.json();
        if (response.ok && data.success && !cancelled) {
          setAdminUserCount(data.data.adminUserCount || 0);
          setMobileUserCount(data.data.mobileUserCount || 0);
        }
      } catch {
        // Leave the seat card at its 0 defaults if the stats call fails.
      }
    };

    fetchLedger();
    fetchSeatDistribution();
    return () => { cancelled = true; };
  }, []);

  // Filter the live ledger rows
  const filteredFactories = ledgerRows.filter(f => {
    const matchesSearch = f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          f.region.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || f.subStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="flex flex-col gap-8 animate-fade-in font-sans">
      {/* Header Info */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Subscription Plan & Contract Intelligence</h2>
          <p className="text-xs text-slate-500 mt-1">Audit status, active seat pools, and upcoming contract expirations across enterprise clusters.</p>
        </div>
      </div>

      {/* Subscription KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Nearing Expiry Card */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-2xl p-6 border border-amber-200 shadow-sm flex flex-col justify-between hover:shadow-md transition-all relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/5 rounded-bl-full pointer-events-none" />
          <div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full uppercase tracking-wider font-mono">Nearing Expiry</span>
              <span className="material-symbols-outlined text-amber-500 text-xl">gavel</span>
            </div>
            <h3 className="text-3xl font-black text-slate-900 tracking-tight leading-none mb-1">{nearingExpiryList.length}</h3>
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 leading-tight">Contracts &lt; 15 Days Left</p>
            <div className="h-0.5 bg-amber-100 rounded-full my-3" />
          </div>
          <div>
            {nearingExpiryList.length > 0 ? (
              <div className="space-y-1">
                {nearingExpiryList.slice(0, 3).map((f, i) => (
                  <p key={i} className="text-[10px] font-mono text-amber-850 truncate leading-relaxed flex items-center gap-1.5 font-bold">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                    {f.name} ({f.daysRemaining}d left)
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-slate-500 font-medium">All systems stable and licensed.</p>
            )}
          </div>
        </motion.div>

        {/* Active Accounts Card */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
          className="bg-white rounded-2xl p-6 border border-emerald-200 shadow-sm flex flex-col justify-between hover:shadow-md transition-all relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-bl-full pointer-events-none" />
          <div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full uppercase tracking-wider font-mono">Active Licenses</span>
              <span className="material-symbols-outlined text-emerald-500 text-xl">verified</span>
            </div>
            <h3 className="text-3xl font-black text-slate-900 tracking-tight leading-none mb-1">{activeCount}</h3>
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 leading-tight">Active Contracts</p>
            <div className="h-0.5 bg-emerald-100 rounded-full my-3" />
          </div>
          <p className="text-[10px] text-slate-500 font-medium leading-relaxed">
            Consistently active production facilities synced with central cluster.
          </p>
        </motion.div>

        {/* Trial & Grace Period Card */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-white rounded-2xl p-6 border border-blue-200 shadow-sm flex flex-col justify-between hover:shadow-md transition-all relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/5 rounded-bl-full pointer-events-none" />
          <div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] font-bold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full uppercase tracking-wider font-mono">Trial &amp; Grace</span>
              <span className="material-symbols-outlined text-blue-500 text-xl">timelapse</span>
            </div>
            <h3 className="text-3xl font-black text-slate-900 tracking-tight leading-none mb-1">{trialCount + graceCount}</h3>
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 leading-tight">{trialCount} Trial, {graceCount} Grace Slots</p>
            <div className="h-0.5 bg-blue-100 rounded-full my-3" />
          </div>
          <p className="text-[10px] text-slate-500 font-medium leading-relaxed">
            Automatic trial seats configured. High premium seat conversion rate.
          </p>
        </motion.div>

        {/* Total Allowed Seats Card */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
          className="bg-white rounded-2xl p-6 border border-purple-200 shadow-sm flex flex-col justify-between hover:shadow-md transition-all relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/5 rounded-bl-full pointer-events-none" />
          <div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] font-bold text-purple-700 bg-purple-50 px-2.5 py-1 rounded-full uppercase tracking-wider font-mono">Seat Distribution</span>
              <span className="material-symbols-outlined text-purple-500 text-xl">airline_seat_recline_normal</span>
            </div>
            <h3 className="text-3xl font-black text-slate-900 tracking-tight leading-none mb-1">{totalSeats}</h3>
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 leading-tight">Total Platform Users</p>
            <div className="h-0.5 bg-purple-100 rounded-full my-3" />
          </div>
          <div className="flex items-stretch gap-2">
            <div className="flex-1 bg-slate-50/70 rounded-xl px-3 py-2 border border-slate-100">
              <div className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[14px] text-purple-500">admin_panel_settings</span>
                <span className="text-lg font-black leading-none text-slate-900">{adminUserCount}</span>
              </div>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mt-1">Admin Users</p>
            </div>
            <div className="flex-1 bg-slate-50/70 rounded-xl px-3 py-2 border border-slate-100">
              <div className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[14px] text-purple-500">smartphone</span>
                <span className="text-lg font-black leading-none text-slate-900">{mobileUserCount}</span>
              </div>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mt-1">Mobile Users</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Contract Detailed Log & Table Component */}
      <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm">
        {/* Section Header */}
        <div className="mb-6 pb-4 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-2">
          <div>
            <h3 className="text-base font-bold text-slate-900">Licensing Log & Contracts Ledger</h3>
            <p className="text-xs text-slate-400 mt-0.5">Explore active terms, dates, and live remaining contract durations</p>
          </div>
        </div>

        {/* Professional Filters and Search Toolbar Row */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          {/* Status Tabs Group */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mr-1">Filter state</span>
            <div className="bg-slate-100 p-1 rounded-xl flex flex-wrap gap-1">
              {['All', 'Active', 'Trial', 'Grace', 'Expired'].map((opt) => (
                <button
                  key={opt}
                  onClick={() => setStatusFilter(opt)}
                  className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all ${
                    statusFilter === opt 
                      ? 'bg-white text-[#136bce] shadow-sm' 
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Keyword Search Input with perfect vertical alignment & padding */}
          <div className="relative w-full md:max-w-xs">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Filter by factory name..."
              className="w-full bg-slate-50 text-xs rounded-xl border border-slate-200 pl-10 pr-4 py-2 text-slate-800 placeholder-slate-400 focus:bg-white focus:ring-1 focus:ring-[#136bce] focus:border-[#136bce] focus:outline-none transition-all"
            />
          </div>
        </div>

        {/* Contract Table */}
        <div className="overflow-x-auto">
          {isLoadingLedger ? (
            <div className="flex flex-col items-center justify-center p-12 text-slate-400 border border-dashed border-slate-200 rounded-2xl">
              <span className="material-symbols-outlined text-[36px] mb-2 text-slate-300 animate-spin">progress_activity</span>
              <p className="text-xs font-bold tracking-wide">Loading licensing ledger…</p>
            </div>
          ) : ledgerError ? (
            <div className="flex flex-col items-center justify-center p-12 text-rose-500 border border-dashed border-rose-200 rounded-2xl">
              <span className="material-symbols-outlined text-[36px] mb-2 text-rose-300">cloud_off</span>
              <p className="text-xs font-bold tracking-wide">{ledgerError}</p>
            </div>
          ) : filteredFactories.length > 0 ? (
            <table className="w-full text-left border-collapse min-w-[900px]">
              <thead>
                <tr className="border-b border-slate-200 text-slate-400 text-[10px] font-bold uppercase tracking-wider">
                  <th className="py-4 px-4 w-1/4">Factory Node</th>
                  <th className="py-4 px-4 text-center">Status</th>
                  <th className="py-4 px-4 text-center">Start Date</th>
                  <th className="py-4 px-4 text-center">Expiry Date</th>
                  <th className="py-4 px-4 text-center">Capacity Limit</th>
                  <th className="py-4 px-4 text-center">Days Remaining</th>
                  <th className="py-4 px-4 text-right">Licensing Health</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredFactories.map((fac, idx) => {
                  const isExpiringSoon = fac.daysRemaining > 0 && fac.daysRemaining <= 30 && fac.subStatus !== 'Expired';
                  const isExpired = fac.subStatus === 'Expired';
                  
                  return (
                    <tr key={fac.name} className="hover:bg-slate-50/70 transition-colors">
                      {/* Name & Region */}
                      <td className="py-4 px-4">
                        <p className="text-xs font-bold text-slate-900 font-sans">{fac.name}</p>
                        <p className="text-[10px] text-slate-450 flex items-center gap-1 font-medium select-none">
                          <span className="material-symbols-outlined text-[12px] text-[#136bce]">location_on</span>
                          {fac.region}
                        </p>
                      </td>

                      {/* Status Badge */}
                      <td className="py-4 px-4 text-center">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border select-none ${
                          fac.subStatus === 'Trial' ? 'bg-blue-50/70 text-blue-700 border-blue-200' :
                          fac.subStatus === 'Active' ? 'bg-emerald-50/70 text-emerald-700 border-emerald-200' :
                          fac.subStatus === 'Grace' ? 'bg-amber-50/70 text-amber-700 border-amber-200' :
                          'bg-rose-50/70 text-rose-700 border-rose-200'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            fac.subStatus === 'Trial' ? 'bg-blue-500' :
                            fac.subStatus === 'Active' ? 'bg-emerald-500' :
                            fac.subStatus === 'Grace' ? 'bg-amber-500' :
                            'bg-red-500'
                          }`} />
                          {fac.subStatus}
                        </span>
                      </td>

                      {/* Subscription Start Date */}
                      <td className="py-4 px-4 text-center text-xs font-bold text-slate-600 font-mono">
                        {fac.start}
                      </td>

                      {/* Subscription Expiry Date */}
                      <td className="py-4 px-4 text-center text-xs font-bold text-slate-600 font-mono">
                        {fac.end}
                      </td>

                      {/* Allowed Seats */}
                      <td className="py-4 px-4 text-center text-xs font-bold text-slate-800 font-mono">
                        {fac.allowedUsers || 20} Seats
                      </td>

                      {/* Days Remaining Banner */}
                      <td className="py-4 px-4 text-center font-mono">
                        {isExpired ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-md bg-rose-50 text-red-650 font-bold border border-rose-100">
                            Expired
                          </span>
                        ) : isExpiringSoon ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 font-bold border border-amber-200 animate-pulse-subtle">
                            {fac.daysRemaining} days left
                          </span>
                        ) : (
                          <span className="text-xs font-bold text-slate-700">
                            {fac.daysRemaining} Days
                          </span>
                        )}
                      </td>

                      {/* System Licensing Health indicator */}
                      <td className="py-4 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <span className={`text-[10px] font-bold uppercase tracking-widest ${
                            isExpired ? 'text-red-500' :
                            isExpiringSoon ? 'text-amber-500' : 'text-emerald-500'
                          }`}>
                            {isExpired ? 'Terminated' : isExpiringSoon ? 'Renewal Due' : 'Protected'}
                          </span>
                          <span className={`w-2.5 h-2.5 rounded-full ${
                            isExpired ? 'bg-red-500' :
                            isExpiringSoon ? 'bg-amber-400' : 'bg-emerald-500'
                          }`} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center justify-center p-12 text-slate-400 border border-dashed border-slate-200 rounded-2xl">
              <span className="material-symbols-outlined text-[36px] mb-2 text-slate-300">search_off</span>
              <p className="text-xs font-bold tracking-wide">No factory subscription matches current search parameters.</p>
              <button 
                onClick={() => { setSearchTerm(''); setStatusFilter('All'); }} 
                className="mt-3 text-xs text-[#136bce] font-bold hover:underline"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
